import {
  Divider,
  HelpText,
  InlineAlert,
  Label,
  Tag,
  TextField,
} from '@lego/connect-components-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormPreviewResponse, FormState, LabelData } from '../../types/types';
import {
  formatPreviewTypeLabel,
  getPreviewFieldPlaceholder,
  isFieldMappedToVariable,
} from './utils';

// ─── Sub-components ───────────────────────────────────────────────────────────

const MetaField = ({ label, value }: { label: string; value: string }) => (
  <div>
    <Label size="small">{label}</Label>
    <div className="mt-50 text-[15px] font-semibold text-core-slate-1300">{value}</div>
  </div>
);

const PreviewField = ({
  field,
}: {
  field: {
    id: string;
    title: string;
    badge: string;
    helper: string;
    setValue: string;
    placeholder: string;
  };
}) => (
  <div>
    <div className="mb-100 flex items-center gap-100">
      <span className="text-[13px] font-semibold text-core-slate-1300">{field.title}</span>
      <Tag size="small" variant="neutral">
        {field.badge}
      </Tag>
    </div>
    <HelpText size="small">{field.helper}</HelpText>
    <TextField readOnly size="medium" value={field.setValue} placeholder={field.placeholder} />
  </div>
);

// ─── Main component ──────────────────────────────────────────────────────────

type FormPreviewProps = {
  formState: Partial<FormState>;
  selectedLabel: LabelData | null;
  printers: { items: { id: number; name: string }[] } | undefined;
  previewResult: FormPreviewResponse | null;
};

const FormPreview = ({ formState, selectedLabel, printers, previewResult }: FormPreviewProps) => {
  const { t } = useTranslation();
  const { name, labelRevisions, printerIds, variables, fields } = formState;

  const selectedPrinterLabel = useMemo(() => {
    if (!printerIds?.length) return t('formPage.preview.noPrinterSelected');
    const names = printerIds
      .map((id) => printers?.items.find((p) => p.id === id)?.name)
      .filter((n): n is string => Boolean(n));
    if (names.length === 0)
      return t('formPage.preview.selectedPrintersCount', { count: printerIds.length });
    if (names.length === 1) return names[0];
    return t('formPage.preview.selectedPrintersCount', { count: names.length });
  }, [printerIds, printers?.items, t]);

  const selectedRevisionLabel = labelRevisions?.[0] || t('formPage.preview.noRevisionSelected');

  const previewFields = useMemo(
    () =>
      (variables ?? [])
        .filter((v) => v.fieldId.trim())
        .map((variable) => {
          const mappedField = fields?.find((field) => isFieldMappedToVariable(field, variable));
          const fieldType = variable.type || mappedField?.type || 'String';
          const setValueAction = variable.actions?.find((a) => a.type === 'set_value');
          const setValue =
            typeof setValueAction?.config?.value === 'string' ? setValueAction.config.value : '';

          return {
            id: variable.id,
            title: variable.name.trim() || mappedField?.name || 'Variable',
            badge: fieldType.toLowerCase(),
            helper: t('formPage.preview.fieldValueType', {
              type: formatPreviewTypeLabel(fieldType),
            }),
            placeholder: getPreviewFieldPlaceholder(fieldType),
            setValue,
          };
        }),
    [fields, t, variables]
  );

  const previewMessages =
    previewResult?.variableErrors.map((e: { message: string }) => e.message) ?? [];

  return (
    <div className="flex flex-col gap-400">
      {/* Validation Result */}
      {previewResult &&
        (previewResult.valid ? (
          <InlineAlert variant="success">
            <InlineAlert.Title>{t('formPage.preview.validationSuccess')}</InlineAlert.Title>
          </InlineAlert>
        ) : (
          <InlineAlert variant="negative">
            <InlineAlert.Title>{t('formPage.preview.validationFailure')}</InlineAlert.Title>
            {previewMessages.length > 0 && (
              <InlineAlert.Content>
                <ul className="m-0 list-disc pl-300">
                  {previewMessages.map((msg: string) => (
                    <li key={msg} className="text-[13px]">
                      {msg}
                    </li>
                  ))}
                </ul>
              </InlineAlert.Content>
            )}
          </InlineAlert>
        ))}

      {/* Meta Info */}
      <div className="grid grid-cols-3 gap-x-400 gap-y-300">
        <MetaField
          label={t('formPage.preview.templateName')}
          value={name?.trim() || t('formPage.preview.untitledForm')}
        />
        <MetaField
          label={t('formPage.preview.labelName')}
          value={selectedLabel?.name || t('formPage.preview.noLabelSelected')}
        />
        <MetaField label={t('formPage.preview.labelRevision')} value={selectedRevisionLabel} />
        <MetaField label={t('formPage.preview.selectPrinter')} value={selectedPrinterLabel} />
      </div>

      <Divider variant="subtle" />

      {/* Preview Fields */}
      {previewFields.length > 0 ? (
        <div className="grid grid-cols-2 gap-400 lg:grid-cols-3">
          {previewFields.map((field) => (
            <PreviewField key={field.id} field={field} />
          ))}
        </div>
      ) : (
        <div className="rounded-[12px] border border-dashed border-core-slate-100 bg-core-slate-50 px-400 py-500 text-center text-[13px] text-core-slate-900/55">
          {t('formPage.preview.emptyState')}
        </div>
      )}
    </div>
  );
};

export default FormPreview;
